import { Pill, ChevronUp, ChevronDown } from 'lucide-react';
import { useExpandable } from '../../../hooks/ui/useExpandable';

// HTML 엔티티(&nbsp;, &#x2219; 등) + 태그 → 실제 문자로 디코딩
const decodeHtml = text => {
  if (!text) return text;
  try {
    const doc = new DOMParser().parseFromString(String(text), 'text/html');
    return doc.body.textContent;
  } catch {
    return String(text);
  }
};

const DETAIL_FIELDS = [
  { label: '효능·효과', key: 'efcy_qesitm' },
  { label: '복용 방법', key: 'use_method_qesitm' },
  { label: '주요 부작용', key: 'atpn_qesitm' },
  { label: '보관 방법', key: 'storage_method' },
  { label: '상호작용', key: 'intrc_qesitm' },
];

/* v2 픽토그램 라벨 — 90w 프레임 안에 SVG + 라벨.
   ponytail: 현재 4종만 v2 에셋 존재 — 매핑 없는 코드는 레거시 jpg로 폴백,
   디자인팀이 전체 세트 주면 이 맵만 확장 */
const PICTOGRAM_V2 = {
  meal: { label: '식사와 함께' },
  'no-grapefruit': { label: '자몽주스 금지' },
  'no-alcohol': { label: '알코올 금지' },
  lifestyle: { label: '생활요법 병행' },
};

/* 단일 약품 카드 — 자체 펼침 토글 */

export const MedicationGuideCard = ({ med }) => {
  const { expanded, toggle: toggleExpanded } = useExpandable(false);
  const picCodes = (med.pic_codes || '')
    .split(',')
    .map(c => c.trim())
    .filter(Boolean);

  // 상세 정보 리스트: 응답에 값이 있는 필드만 (HTML decode + 트림)
  const detailItems = DETAIL_FIELDS.map(f => ({
    label: f.label,
    body: decodeHtml(med[f.key]),
  })).filter(it => it.body && String(it.body).trim().length > 0);

  return (
    <article
      onClick={toggleExpanded}
      className="bg-white flex flex-col gap-4 w-full cursor-pointer"
      style={{
        border: '1px solid #e4e4e7',
        borderRadius: 10,
        padding: 16,
        boxShadow: '0px 1px 3px rgba(15,13,13,0.06)',
      }}
    >
      {/* medicine 행 — Figma 15:6477: 이미지 91x60 + 약명 SB15 + 성분명 R12 + 제약사 R14 */}
      <div
        className="flex items-center gap-4 w-full"
        style={{ paddingRight: 16 }}
      >
        <div
          className="flex items-center justify-center shrink-0"
          style={{
            width: 91,
            height: 60,
            background: med.image_url || med.item_image || med.thumbnail_url ? '#cfcfcf' : '#eff1f3',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          {med.image_url || med.item_image || med.thumbnail_url ? (
            <img
              src={med.image_url || med.item_image || med.thumbnail_url}
              alt={med.item_name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={e => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <Pill size={28} className="text-zinc-400" />
          )}
        </div>
        {/* name (gap-2) */}
        <div className="flex flex-1 flex-col gap-0.5 justify-center min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {/* 긴 약 이름(예: '트로나점안액(…)(수출명:…)')이 카드 밖으로 나가지 않게 한 줄 말줄임 */}
            <span
              className="truncate max-w-full"
              title={med.item_name || med.name || ''}
              style={{
                fontSize: 'var(--text-15)',
                fontWeight: 600,
                color: '#18181b',
                lineHeight: '22px',
                letterSpacing: '-0.3px',
              }}
            >
              {med.item_name || med.name || '-'}
            </span>
            {(med.ingredient_name || med.main_item_ingr) && (
              <span
                className="whitespace-nowrap"
                style={{ fontSize: 'var(--text-12)', color: '#71717a', lineHeight: '18px' }}
              >
                {med.ingredient_name || med.main_item_ingr}
              </span>
            )}
          </div>
          <span
            className="truncate"
            style={{
              fontSize: 'var(--text-14)',
              color: '#71717a',
              lineHeight: '20px',
              letterSpacing: '-0.28px',
            }}
          >
            {med.entp_name || med.maker || ''}
          </span>
        </div>
      </div>

      {/* warning 영역 — Figma 767:27358. 미매핑 코드는 레거시 jpg(텍스트 포함) */}
      {picCodes.length > 0 && (
        <div className="flex flex-col gap-2 w-full">
          <p
            style={{
              fontSize: 'var(--text-14)',
              fontWeight: 600,
              color: '#18181b',
              lineHeight: '20px',
              letterSpacing: '-0.28px',
            }}
          >
            주의사항
          </p>

          {/* Figma 767:27358 list — 90w 프레임 border #e4e4e7 r12, 텍스트가 이미지 안에 포함. gap 8(세로)/12(가로) */}
          <div className="flex items-start flex-wrap gap-y-2 gap-x-3">
            {picCodes.map(code => (
              <div
                key={code}
                className="shrink-0 overflow-hidden flex flex-col items-center"
                style={{
                  width: 90,
                  border: '1px solid #e4e4e7',
                  borderRadius: 12,
                  background: '#fff',
                }}
              >
                {PICTOGRAM_V2[code] ? (
                  <>
                    <img
                      src={`/images/pictogram/v2/${code}.svg`}
                      alt={PICTOGRAM_V2[code].label}
                      style={{ width: 60, height: 60, marginTop: 12 }}
                    />
                    <p
                      className="text-center w-full"
                      style={{ fontSize: 'var(--text-12)', color: '#3f3f46', lineHeight: '18px', padding: '4px 6px 12px' }}
                    >
                      {PICTOGRAM_V2[code].label}
                    </p>
                  </>
                ) : (
                  <img
                    src={`/images/pictogram/${code}.jpg`}
                    alt={code}
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                    onError={e => {
                      e.target.closest('div').style.display = 'none';
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* accordion — Figma 545:13010 펼침 상태 */}
      {expanded ? (
        <div className="flex flex-col gap-3 w-full">
          {/* 상세 정보 헤더 — border-t pt-12 */}
          <div
            className="flex items-center justify-between w-full"
            style={{ borderTop: '1px solid #e4e4e7', paddingTop: 12 }}
          >
            <p
              style={{
                fontSize: 'var(--text-14)',
                fontWeight: 600,
                color: '#3f3f46',
                lineHeight: '20px',
                letterSpacing: '-0.28px',
              }}
            >
              상세 정보
            </p>
          </div>

          {/* 상세 정보 리스트 — 각 항목 py-8 gap-4 */}
          <div className="flex flex-col w-full">
            {detailItems.length === 0 ? (
              <p
                className="text-center"
                style={{ fontSize: 'var(--text-12)', color: '#a1a1aa', padding: '16px 0' }}
              >
                상세 정보가 없습니다.
              </p>
            ) : (
              detailItems.map(it => (
                <div
                  key={it.label}
                  className="bg-white flex flex-col gap-1 items-start w-full"
                  style={{ padding: '8px 0' }}
                >
                  <p
                    className="w-full"
                    style={{
                      fontSize: 'var(--text-14)',
                      fontWeight: 600,
                      color: '#71717a',
                      lineHeight: '20px',
                      letterSpacing: '-0.28px',
                    }}
                  >
                    {it.label}
                  </p>
                  <p
                    className="w-full"
                    style={{
                      fontSize: 'var(--text-14)',
                      fontWeight: 400,
                      color: '#18181b',
                      lineHeight: '22px',
                      letterSpacing: '-0.28px',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {it.body}
                  </p>
                </div>
              ))
            )}
          </div>

          {/* "접기" — border-t pt-12 + ChevronUp (클릭은 외곽 article이 위임 처리) */}
          <div
            className="flex items-center justify-between w-full"
            style={{ borderTop: '1px solid #e4e4e7', paddingTop: 12 }}
          >
            <span
              style={{
                fontSize: 'var(--text-14)',
                fontWeight: 500,
                color: '#71717a',
                lineHeight: '20px',
                letterSpacing: '-0.28px',
              }}
            >
              접기
            </span>
            <ChevronUp size={16} className="text-[#71717a]" strokeWidth={1.5} />
          </div>
        </div>
      ) : (
        <div
          className="flex items-center justify-between w-full"
          style={{ borderTop: '1px solid #e4e4e7', paddingTop: 12 }}
        >
          <span
            style={{
              fontSize: 'var(--text-14)',
              fontWeight: 500,
              color: '#71717a',
              lineHeight: '20px',
              letterSpacing: '-0.28px',
            }}
          >
            상세 정보 보기
          </span>
          <ChevronDown size={16} className="text-[#71717a]" strokeWidth={1.5} />
        </div>
      )}
    </article>
  );
};
